import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Segmented control: troca a VISÃO DOS MESMOS DADOS — Cards/Tabela,
 * Kanban/Lista, Lista/Por dia, Caixa padrão/Peso variável.
 *
 * **Não confundir com `PageTabs`.** Aba troca PÁGINA dentro da página e é
 * sublinhada; o segmentado troca a lente sobre o mesmo conjunto e é pastilha.
 *
 * Antes deste componente o mesmo controle tinha TRÊS aparências: preto em
 * Vendas, Operações e no formulário de venda; verde (`bg-primary`) em Mangarás
 * e Colheitas; e nenhuma delas era a da spec §5.2.
 * As duas foram descartadas por motivo, não por gosto:
 *
 * - **O verde compete com a ação primária.** Em Vendas o segmentado fica a
 *   poucos pixels do botão "Nova venda", e a regra do sistema é que o verde
 *   aparece uma vez por região — na ação.
 * - **O preto vira o elemento mais pesado da tela**, mais alto que a própria
 *   ação primária. Inverte a hierarquia.
 *
 * A pastilha ativa é `bg-background` (100%), **não** `bg-card` (98%): contra o
 * trilho `bg-muted` o card some. Isso foi descoberto no #174 — não repita.
 */
export interface SegmentedOpcao<T extends string> {
  id: T;
  label: string;
  icone?: LucideIcon;
}

export interface SegmentedProps<T extends string> {
  opcoes: SegmentedOpcao<T>[];
  valor: T;
  onChange: (v: T) => void;
  className?: string;
  /** Vai no trilho; cada item ganha `<prefixoTestId>-<id>` sozinho. */
  testId?: string;
  /** Prefixo do testid de cada item. Existe para não quebrar quem já tinha um
   *  nome próprio — o formulário de venda usa `chip-modo-`. */
  prefixoTestId?: string;
}

export function Segmented<T extends string>({
  opcoes,
  valor,
  onChange,
  className,
  testId,
  prefixoTestId = "seg",
}: SegmentedProps<T>) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md bg-muted p-0.5 h-9",
        className,
      )}
      role="tablist"
      data-testid={testId}
    >
      {opcoes.map(opcao => {
        const Icone = opcao.icone;
        const ativa = opcao.id === valor;
        return (
          <button
            key={opcao.id}
            type="button"
            role="tab"
            aria-selected={ativa}
            onClick={() => onChange(opcao.id)}
            data-testid={`${prefixoTestId}-${opcao.id}`}
            className={cn(
              "h-8 px-3 rounded-[4px] text-sm inline-flex items-center gap-1.5 whitespace-nowrap transition-colors",
              ativa
                ? "bg-background text-foreground font-semibold shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {Icone && <Icone className="h-3.5 w-3.5" />}
            {opcao.label}
          </button>
        );
      })}
    </div>
  );
}
